import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { PurchasesModal } from '../../pages/PurchasesPage';

// Use relative URL so Vite proxy handles routing to backend (avoids CORS issues)
// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ActivityItem {
  id: string;
  type: 'purchase';
  shares: number;
  location?: string;
  flag?: string;
  timestamp: Date;
  message: string;
}

interface CountryMap {
  [code: string]: { name: string; flag: string };
}

/**
 * Live activity feed showing REAL anonymized shareholder activity from the database
 */
export const LiveActivityFeed: React.FC = () => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const countryMapRef = useRef<CountryMap>({});
  const lastFetchedIdRef = useRef<string | null>(null);

  // Load country mapping via API
  const loadCountryMap = useCallback(async () => {
    try {
      const response = await fetch(`/api/public/phase-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'get_country_map' })
      });

      const result = await response.json();
      if (response.ok && result.data?.countries) {
        const map: CountryMap = {};
        result.data.countries.forEach((c: any) => {
          map[c.country_code] = { name: c.country_name, flag: c.flag_emoji || '🌍' };
        });
        countryMapRef.current = map;
      }
    } catch (err) {
      console.error('Error loading country map:', err);
    }
  }, []);

  // Fetch real purchase activity from API endpoint
  const fetchRealActivity = useCallback(async (isInitial = false) => {
    try {
      const limit = isInitial ? 5 : 1;
      const response = await fetch(`/api/live-activity?limit=${limit}`);

      if (!response.ok) {
        console.error('Error fetching activity: API returned', response.status);
        return;
      }

      const result = await response.json();

      if (!result.success || !result.activities) {
        console.error('Error fetching activity:', result.error);
        return;
      }

      const data = result.activities;

      if (data && data.length > 0) {
        // Filter out already seen activities
        const newData = isInitial
          ? data
          : data.filter((d: any) => d.id !== lastFetchedIdRef.current);

        if (newData.length > 0) {
          lastFetchedIdRef.current = newData[0].id;

          const newActivities: ActivityItem[] = newData.map((purchase: any) => {
            const countryCode = purchase.country || 'ZAF';
            const country = countryMapRef.current[countryCode] || { name: 'Africa', flag: '🌍' };
            const shares = purchase.shares;

            return {
              id: purchase.id,
              type: 'purchase' as const,
              shares,
              location: country.name,
              flag: country.flag,
              timestamp: new Date(purchase.timestamp),
              message: `Someone in ${country.name} purchased ${shares.toLocaleString()} shares`
            };
          });

          if (isInitial) {
            setActivities(newActivities);
          } else {
            setActivities(prev => [...newActivities, ...prev.slice(0, 4)]);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching real activity:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      await loadCountryMap();
      await fetchRealActivity(true);
    };
    init();
  }, [loadCountryMap, fetchRealActivity]);

  // Poll for new activity every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRealActivity(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchRealActivity]);

  const getTimeAgo = useCallback((date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 120) return '1 minute ago';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    return `${Math.floor(seconds / 3600)} hours ago`;
  }, []);

  if (!isVisible) return null;

  return (
    <div className="live-activity-feed">
      <div className="feed-header">
        <div className="feed-title">
          <span className="live-indicator">●</span>
          <span>Live Shareholder Activity</span>
        </div>
        <button
          className="feed-close"
          onClick={() => setIsVisible(false)}
          aria-label="Close activity feed"
        >
          ×
        </button>
      </div>

      <div className="feed-content">
        {isLoading ? (
          <div className="activity-item">
            <span className="activity-icon">⏳</span>
            <div className="activity-details">
              <span className="activity-message">Loading recent activity...</span>
            </div>
          </div>
        ) : activities.length === 0 ? (
          <div className="activity-item">
            <span className="activity-icon">📊</span>
            <div className="activity-details">
              <span className="activity-message">No recent purchases yet</span>
            </div>
          </div>
        ) : (
          activities.map((activity, index) => (
            <div
              key={activity.id}
              className={`activity-item ${index === 0 ? 'new' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <span className="activity-icon">{activity.flag || '💰'}</span>
              <div className="activity-details">
                <span className="activity-message">{activity.message}</span>
                <span className="activity-time">{getTimeAgo(activity.timestamp)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="feed-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="activity-count">
          {activities.length} recent purchase{activities.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: '#000',
            border: 'none',
            borderRadius: 8,
            padding: '6px 16px',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(245,158,11,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          See All Purchases →
        </button>
      </div>

      {showModal && ReactDOM.createPortal(
        <PurchasesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          countryMap={countryMapRef.current}
        />,
        document.body
      )}
    </div>
  );
};

export default LiveActivityFeed;

